import React, {forwardRef, useImperativeHandle} from 'react';
import {TextField, TextFieldProps} from '../TextField';
import {BaseDatePicker, BaseDatePickerProps, BaseDatePickerRef} from './BaseDatePicker';
import {DatePickerInput} from './DatePickerInput';
import Flatpickr from 'react-flatpickr';
import {useControlled} from '@mui/material';
import {setDateFormat} from '../helpers/dateUtils';

export interface DatePickerProps<T = TextFieldProps> extends Omit<BaseDatePickerProps, 'render' | 'mode'> {
    /**
      * IDs are automatically generated by component for accessibility purposes.
      * Use this prop to pass a specific ID value that will be appended.
      * @default "";
      *
      */
    id?: string;
    /**
     * Placeholder text to the single mode DatePicker.
     */
    placeholder?: string;
    /**
     * Type currently defaults to TextFieldProps. However, if you change
     * the `renderInput`, we suggest to also update the type parameter.
     * For example, we use TextFieldProps because the renderInput function returns a TextField.
     * @default TextFieldProps
     */
    InputProps?: T;
    /**
      * The default React Input Component used to render the input.
      * @default TextField
      */
    InputComponent?: React.ComponentType<T>;
    // TODO: For Future
    // /**
    //  * Switch between 'single' or 'time'. Setting mode to 'time' appends a
    //  * time picker to the calendar popover.
    //  * @default 'single'
    //  */
    // mode?: Extract<ModifiedFlatpickrOptions['mode'], 'single' | 'time'>;
}

// ** TODO: figure out what translation strategy will be
const setPlaceholderText = (enableTime: boolean | undefined, noCalendar: boolean | undefined): string => {
    if (enableTime && noCalendar || noCalendar) {
        return 'Select Time';
    } else if (enableTime) {
        return 'Select Date and Time';
    }
    return 'Select Date';
};

export const DatePicker = forwardRef<BaseDatePickerRef, DatePickerProps>(({
    enableTime, noCalendar, InputComponent = TextField, InputProps, placeholder,
    size = 'medium', id = '', dateFormat: userDateFormat,
    value: propsValue, defaultValue: propsDefaultValue, onChange, ...props
}, ref) => {
    const fpRef = React.useRef<Flatpickr['flatpickr']>(null);
    const dateFormat = userDateFormat || setDateFormat(enableTime, noCalendar);

    useImperativeHandle<BaseDatePickerRef, BaseDatePickerRef>(ref, () => fpRef.current);

    const normalizedValue = React.useMemo(() => Array.isArray(propsValue) ? propsValue[0] : propsValue, [propsValue]);
    const normalizedDefaultvalue = React.useMemo(() => Array.isArray(propsDefaultValue) ? propsDefaultValue[0] : propsDefaultValue, [propsDefaultValue]);
    const [value, setValue] = useControlled({
        controlled: normalizedValue,
        default: normalizedDefaultvalue,
        name: 'value'
    });
    const renderComponent: BaseDatePickerProps['render'] = React.useCallback((_, renderRef): React.ReactElement => (
        <DatePickerInput
            InputComponent={InputComponent}
            InputProps={InputProps}
            dateFormat={dateFormat}
            fpRef={fpRef}
            id={id}
            placeholder={placeholder ? placeholder : setPlaceholderText(enableTime, noCalendar)}
            renderRef={renderRef}
            size={size}
            value={value}
        />
    ), [
        InputComponent,
        InputProps,
        dateFormat,
        fpRef,
        id,
        placeholder,
        size,
        value,
        enableTime,
        noCalendar
    ]);

    return (
        <BaseDatePicker
            {...props}
            dateFormat={dateFormat}
            enableTime={enableTime}
            noCalendar={noCalendar}
            onChange={(dates, ...args): void => {
                setValue(dates[0]);
                onChange?.(dates, ...args);
            }}
            ref={fpRef}
            render={renderComponent}
            size={size}
            value={value}
        />
    );
});

DatePicker.displayName = 'DatePicker';
